import type { ReactNode } from 'react';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '@/shared/components/dashboard/panels';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { cn } from '@/shared/lib/cn';

interface AnalyticsPanelProps {
  title: string;
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  errorMessage: string;
  emptyTitle: string;
  emptyMessage?: string;
  children: ReactNode;
  className?: string;
}

export function AnalyticsPanel({
  title,
  isLoading,
  isError,
  isEmpty,
  errorMessage,
  emptyTitle,
  emptyMessage,
  children,
  className,
}: AnalyticsPanelProps) {
  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingPanel className="py-10" />
        ) : isError ? (
          <ErrorPanel message={errorMessage} className="border-0 shadow-none" />
        ) : isEmpty ? (
          <EmptyPanel
            title={emptyTitle}
            {...(emptyMessage ? { message: emptyMessage } : {})}
            className="border-0 shadow-none"
          />
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

interface AnalyticsTableProps {
  columns: Array<{ key: string; label: string; align?: 'start' | 'end' }>;
  rows: Array<{ key: string; cells: ReactNode[] }>;
}

export function AnalyticsTable({ columns, rows }: AnalyticsTableProps) {
  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full min-w-[320px] text-sm">
        <thead>
          <tr className="border-b border-border text-muted-foreground">
            {columns.map((column) => (
              <th
                key={column.key}
                className={cn(
                  'px-2 py-2 font-medium',
                  column.align === 'end' ? 'text-end' : 'text-start',
                )}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-b border-border/60 last:border-0">
              {row.cells.map((cell, index) => (
                <td
                  key={`${row.key}-${index}`}
                  className={cn(
                    'px-2 py-2.5 tabular-nums',
                    columns[index]?.align === 'end' ? 'text-end' : 'text-start',
                    index === 0 && 'font-medium text-foreground',
                  )}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
